import React, { useState } from 'react'
import { PencilSquareIcon, TrashIcon, QrCodeIcon, PrinterIcon } from '@heroicons/react/24/outline'
import BlockMapComponent from 'components/common/BlockMapComponent'
// import TableComponent from 'components/common/TableComponent'
import 'styles/BlockTable.css'
import BlockQRModal from 'components/modals/BlockQRModal'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'

import { formatNumbers } from 'utils'

const BlockTable = ({
  filteredBlocks,
  blockGroups,
  onEdit,
  onDelete,
  toggleIsTunnelRowModalOpen,
  tunnelRefreshRef
}) => {
  const [selectedBlockId, setSelectedBlockId] = useState(null)
  const [isQRModalOpen, setIsQRModalOpen] = useState(false)
  const [selectedBlock, setSelectedBlock] = useState(null)
  const [printingBlockId, setPrintingBlockId] = useState(null)

  const handleBlockClick = (blockId) => {
    setSelectedBlockId(selectedBlockId === blockId ? null : blockId)
  }

  const getBlockGroupName = (bgId) => {
    const group = blockGroups.find((bg) => bg.blockGroupId === bgId)
    return group?.groupName || ''
  }

  const getUnitsPerArea = (totalUnits, area) => {
    return Number(parseFloat(totalUnits) / parseFloat(area)).toFixed(2)
  }

  const handleOpenQRModal = (block) => {
    setSelectedBlock(block)
    setIsQRModalOpen(true)
  }

  const handleCloseQRModal = () => {
    setSelectedBlock(null)
    setIsQRModalOpen(false)
  }

  const handlePrintClick = async (block) => {
    // Prevent multiple clicks by setting loading state
    setPrintingBlockId(block.farmingBlockId)
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/farming-blocks/${block.farmingBlockId}/labels`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        },
        responseType: 'blob' // Important for binary data (PDF)
      })

      // Create a blob URL and download the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${block.name}_labels.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading block labels:', error)
      showAlert({
        text: 'Failed to download block labels',
        icon: 'error'
      })
    } finally {
      // Reset loading state
      setPrintingBlockId(null)
    }
  }

  // const blocksDataTable = {
  //   columns: [
  //     { key: 'farmingBlockId', label: '#' },
  //     {
  //       key: 'name',
  //       label: 'Name',
  //       cell: (block, _, toggleExpand) => (
  //         <button onClick={() => toggleExpand(block.farmingBlockId)} className='clickable'>
  //           {block.name}
  //         </button>
  //       )
  //     },
  //     {
  //       key: 'totalPlants',
  //       label: 'Total Plants',
  //       cell: block => formatNumbers(block.totalPlants)
  //     },
  //     {
  //       key: 'area',
  //       label: 'Area M²',
  //       cell: block => formatNumbers(block.area)
  //     },
  //     {
  //       key: 'unitsPerArea',
  //       label: 'Units/Area',
  //       cell: block => formatNumbers(getUnitsPerArea(block.totalPlants, block.area))
  //     },
  //     { key: 'cropName', label: 'Default Crop' },
  //     { key: 'varietyName', label: 'Default Variety' },
  //     {
  //       key: 'groupName',
  //       label: 'Group Name',
  //       cell: block => getBlockGroupName(block.blockGroupId)
  //     },
  //     {
  //       key: 'actions',
  //       label: 'Actions',
  //       cell: block => (
  //         <div className='flex gap-2'>
  //           <button onClick={() => onEdit({ ...block })}>
  //             <PencilSquareIcon className='w-5 h-5 text-blue-500' />
  //           </button>
  //           <button onClick={() => onDelete(block.farmingBlockId)}>
  //             <TrashIcon className='w-5 h-5 text-red-500' />
  //           </button>
  //         </div>
  //       )
  //     }
  //   ],
  //   rows: filteredBlocks
  // }

  return (
    <div>
      {/* {filteredBlocks.length !== 0 &&
        <TableComponent
          tableData={blocksDataTable}
          renderExpandedRow={(row) => (
            <BlockMapComponent
              toggleIsTunnelRowModalOpen={toggleIsTunnelRowModalOpen}
              {...row}
              farmingBlockId={row.farmingBlockId}
              isHydroponicBlock={row.isHydroponicBlock}
            />
          )}
          getRowId={(row) => row.farmingBlockId}
        />} */}

      <div className='overflow-x-auto max-w-5l'>
        <div className='table-container table-wrapper'>
          <table className='table w-full border-rounded-lg border border-gray-200 text-left'>
            <thead>
              <tr className='cursor-pointer text-greenPrimary hover:bg-gray-100 transition-colors duration-200'>
                <th>#</th>
                <th>Name</th>
                <th>Total Plants</th>
                <th>Area M²</th>
                <th>Units/Area</th>
                <th>Crop</th>
                <th>Variety</th>
                <th>Group Name</th>
                <th>Manage</th>
              </tr>
            </thead>
            <tbody>
              {filteredBlocks.map((block, index) => (
                <React.Fragment key={block.farmingBlockId}>
                  <tr>
                    <td>{block.farmingBlockId}</td>
                    <td className='clickable' onClick={() => handleBlockClick(block.farmingBlockId)}>{block.name}</td>
                    <td>{formatNumbers(block.totalPlants)}</td>
                    <td>{formatNumbers(block.area)}</td>
                    <td>{formatNumbers(getUnitsPerArea(block.totalPlants, block.area))}</td>
                    <td>{block.cropName}</td>
                    <td>{block.varietyName}</td>
                    <td>{getBlockGroupName(block.blockGroupId)}</td>
                    <td>
                      <div className='flex gap-2'>
                        <button onClick={() => handleOpenQRModal(block)} title='QR Code'>
                          <QrCodeIcon className='w-5 h-5 text-green-500' />
                        </button>
                        <button
                          onClick={() => handlePrintClick(block)}
                          title={printingBlockId === block.farmingBlockId ? 'Generating PDF...' : 'Print Labels'}
                          disabled={printingBlockId === block.farmingBlockId}
                          className={`${printingBlockId === block.farmingBlockId ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {printingBlockId === block.farmingBlockId
                            ? (
                              <div className='w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin' />
                              )
                            : (
                              <PrinterIcon className='w-5 h-5 text-gray-600' />
                              )}
                        </button>
                        <button onClick={() => onEdit({ ...block })} title='Edit'>
                          <PencilSquareIcon className='w-5 h-5 text-blue-500' />
                        </button>
                        <button onClick={() => onDelete(block.farmingBlockId)} title='Delete'>
                          <TrashIcon className='w-5 h-5 text-red-500' />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {selectedBlockId === block.farmingBlockId && (
                    <tr>
                      <td colSpan={10}>
                        <BlockMapComponent
                          toggleIsTunnelRowModalOpen={toggleIsTunnelRowModalOpen}
                          tunnelRefreshRef={tunnelRefreshRef}
                          {...filteredBlocks.find(block => block.farmingBlockId === selectedBlockId)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {isQRModalOpen && selectedBlock && (
        <BlockQRModal
          isOpen={isQRModalOpen}
          onClose={handleCloseQRModal}
          blockId={selectedBlock.farmingBlockId}
          blockName={selectedBlock.name}
        />
      )}
    </div>
  )
}

export default BlockTable
